import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR, priceLabel } from './lib';
import { AlternativePicker, RichText, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock, SectionsBlock, TotalsBlock, QuoteMetaBlock } from './SharedBlocks';

const mono = "ui-monospace, 'SF Mono', Menlo, monospace";

const LinearLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;
  const cust = quote.customerData || {};
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '18px 20px' };
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: theme.radius, background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, color: theme.textSecondary, cursor: 'pointer', fontSize: 12 };

  const Row = ({ service, color, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '14px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.45 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              {isOpt && <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 16, height: 16, accentColor: theme.primary, cursor: 'pointer' }} />}
              <p style={{ fontSize: 15, fontWeight: 500, color: theme.textPrimary, margin: 0, letterSpacing: '-0.2px' }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 5, lineHeight: 1.6 }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 500, color: theme.textPrimary, fontFamily: mono, whiteSpace: 'nowrap' }}>{priceLabel(service, lineTotal(service))}{suffix ? <span style={{ color: theme.textMuted }}> {suffix}</span> : null}</span>
        </div>
      </div>
    );
  };
  const Group = ({ title, services, suffix }) => services.length ? (
    <div style={card}>
      <p style={{ fontFamily: mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.primary, margin: '0 0 4px' }}>{title}</p>
      {services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} />)}
    </div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 70px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', maxWidth: 820, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#4ade80' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      {/* HERO with glow */}
      <div style={{ background: `radial-gradient(120% 80% at 50% -10%, ${theme.primary}33 0%, ${theme.bg} 55%)`, padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          {company?.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 28, marginBottom: 26, objectFit: 'contain' }} />
            : <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: theme.textSecondary, marginBottom: 26 }}>{(company?.name || '').toUpperCase()}</p>}
          {cust.company && <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMuted, marginBottom: 14 }}>{cust.company.toUpperCase()}</p>}
          <h1 style={{ fontSize: 38, lineHeight: 1.1, fontWeight: 600, letterSpacing: '-1px', margin: 0, color: theme.textPrimary, maxWidth: 600 }}>
            {quote.heroHeadline || quote.quoteTitle || 'Gebaut für Wachstum.'}
          </h1>
          {quote.quoteIntro && <RichText value={quote.quoteIntro} style={{ fontSize: 16, color: theme.textSecondary, marginTop: 18, lineHeight: 1.7, maxWidth: 560}} />}
          <button onClick={() => setShowAcceptanceForm(true)} className="no-print" style={{ marginTop: 24, padding: '11px 22px', borderRadius: theme.radius, background: `linear-gradient(180deg, ${theme.primary}, ${theme.primary}cc)`, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
            Angebot annehmen
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {quote.customerArguments && (
          <div style={card}>
            <div style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}
        {quote.videoUrl && <div className="no-print" style={card}><video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius }} /></div>}

        <QuoteMetaBlock quote={quote} theme={theme} company={company} />
        <Group title="Einmalige Leistungen" services={einmaligServices} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} suffix="/ Mo" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} suffix="/ Jahr" />

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1, color: theme.textMuted, textTransform: 'uppercase' }}>Gesamt netto</span>
          <span style={{ fontSize: 26, fontWeight: 600, color: theme.textPrimary, fontFamily: mono }}>{formatEUR(einmaligTotals.netto)}</span>
        </div>

        <TermsBlock quote={quote} company={company} theme={theme} />
        <TotalsBlock ctx={ctx} theme={theme} />
        <SectionsBlock quote={quote} theme={theme} ctx={ctx} />
        <AcceptanceBlock ctx={ctx} theme={theme} />
        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FeedbackBlock viewHash={viewHash} theme={theme} />
          <QuestionBlock viewHash={viewHash} theme={theme} />
        </div>
        <FooterBlock company={company} theme={theme} />
      </div>
    </div>
  );
};

export default LinearLayout;
